import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ENABLE_MODAL_SWIPE_BACK } from '../utils/mobileModalSwipe';
import { useNdTheme } from '../utils/ndTheme';
import '../styles/newDesign.css';

// Единая палитра типов события — та же, что в десктопном календаре
const ND_LEAVE_TYPES = {
  vacation: { label: 'Отпуск', emoji: '🌴', cls: 'nd-t-vacation' },
  leave: { label: 'Отгул', emoji: '🚶', cls: 'nd-t-leave' },
  sick: { label: 'Больничный', emoji: '🚩', cls: 'nd-t-sick' },
  remote: { label: 'На удаленке', emoji: '🏠', cls: 'nd-t-remote' },
};

const ND_STATUS_LABELS = {
  approved: 'Одобрено',
  pending: 'Ожидает',
  completed: 'Отработано',
  rejected: 'Отклонено',
};

const months = [
  'Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'
];

const years = Array.from({length: 5}, (_,i) => new Date().getFullYear()-2+i);
const weekDays = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];

// Утилиты
function getDaysMatrix(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month+1, 0);
  const matrix = [];
  let week = [];
  let dayOfWeek = firstDay.getDay();
  
  for(let i=0;i<dayOfWeek;i++) week.push(null);
  for(let d=1;d<=lastDay.getDate();d++) {
    week.push(new Date(year, month, d));
    if(week.length===7) { matrix.push(week); week=[]; }
  }
  if(week.length) { 
    while(week.length<7) week.push(null); 
    matrix.push(week); 
  }
  return matrix;
}

function formatDateToYYYYMMDD(date) {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isDateInRange(targetDate, startDate, endDate) {
  const target = new Date(targetDate);
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  target.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  return target >= start && target <= end;
}

function isToday(d) {
  if (!d) return false;
  const t = new Date();
  return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
}

export default function AllLeavesCalendarMobile({ open, onClose, token, onOpenMobileSidebar }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState({});
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [showUserAutocomplete, setShowUserAutocomplete] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [absentModal, setAbsentModal] = useState({ open: false, date: null, list: [] });
  const [birthdayModal, setBirthdayModal] = useState({ open: false, date: null, list: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar'); // 'calendar' или 'list'
  const [employeesBirthdays, setEmployeesBirthdays] = useState([]);

  const getEmployeeBirthMonthDay = (e) => {
    if (e.birth_month != null && e.birth_day != null) return { month: Number(e.birth_month), day: Number(e.birth_day) };
    const b = (e.birthday || '').trim();
    if (!b) return null;
    const iso = b.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return { month: parseInt(iso[2], 10), day: parseInt(iso[3], 10) };
    const ddmmyy = b.match(/^(\d{1,2})\.(\d{1,2})/);
    if (ddmmyy) return { month: parseInt(ddmmyy[2], 10), day: parseInt(ddmmyy[1], 10) };
    return null;
  };

  const getBirthdaysForDate = (date) => {
    if (!date || !Array.isArray(employeesBirthdays)) return [];
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return employeesBirthdays.filter(e => {
      const md = getEmployeeBirthMonthDay(e);
      return md && md.month === month && md.day === day;
    });
  };

  // Для обработки свайпа
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const modalRef = useRef(null);

  const getLogicalType = (leave) => {
    if (!leave) return 'leave';
    if (leave.type === 'vacation') return 'vacation';
    if (leave.type === 'sick') return 'sick';
    if (leave.type === 'remote') return 'remote';
    if (leave.type === 'leave') {
      const reason = String(leave.reason || '').toLowerCase();
      if (/болею|больнич/i.test(reason)) return 'sick';
      return 'leave';
    }
    return String(leave.type || 'leave');
  };

  const isHr = currentUser && (currentUser.role==='hr'||currentUser.role==='admin'||currentUser.role==='руководитель');

  const filteredLeaves = useMemo(() => {
    return Array.isArray(leaves) ? leaves.filter(l => {
      if (!isHr) {
        if (l.userId !== currentUser?.id) return false;
      } else {
        if (selectedUser !== 'all' && l.userId !== Number(selectedUser)) return false;
      }
      if (selectedStatus !== 'all' && l.status !== selectedStatus) return false;
      const logicalType = getLogicalType(l);
      if (selectedType !== 'all' && logicalType !== selectedType) return false;
      return true;
    }) : [];
  }, [leaves, isHr, currentUser, selectedUser, selectedStatus, selectedType]);

  const monthlyStats = useMemo(() => {
    const stats = { vacation: 0, leave: 0, sick: 0, remote: 0 };
    const processedLeaves = new Set();
    
    for (const leave of filteredLeaves) {
      if (!processedLeaves.has(leave.id)) {
        const startDate = new Date(leave.startDate);
        const endDate = new Date(leave.endDate);
        const currentMonth = new Date(selectedYear, selectedMonth);
        const nextMonth = new Date(selectedYear, selectedMonth + 1);
        
        if (startDate < nextMonth && endDate >= currentMonth) {
          const logicalType = getLogicalType(leave);
          if (logicalType === 'vacation') stats.vacation++;
          else if (logicalType === 'sick') stats.sick++;
          else if (logicalType === 'remote') stats.remote++;
          else if (logicalType === 'leave') stats.leave++;
          processedLeaves.add(leave.id);
        }
      }
    }
    return stats;
  }, [filteredLeaves, selectedYear, selectedMonth]);

  const daysMatrix = useMemo(() => getDaysMatrix(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

  const getAbsentList = (date) => {
    return filteredLeaves.filter(l => {
      const startDateStr = formatDateToYYYYMMDD(l.startDate);
      const endDateStr = formatDateToYYYYMMDD(l.endDate);
      return isDateInRange(date, startDateStr, endDateStr);
    });
  };

  const handleDeleteLeave = async (leaveId) => {
    try {
      const response = await fetch(`/api/leaves/${leaveId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(data => setLeaves(Array.isArray(data) ? data : []));
        alert('Отгул успешно удален');
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Ошибка при удалении отгула:', error);
      alert(`Ошибка при удалении отгула: ${error.message}`);
    }
  };

  // Функция для одобрения заявки
  const handleApproveLeave = async (leaveId) => {
    try {
      const response = await fetch(`/api/leaves/${leaveId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'approved' })
      });

      if (response.ok) {
        // Обновляем список заявок
        fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(data => {
            setLeaves(Array.isArray(data) ? data : []);
            // Обновляем модалку, если она открыта
            if (absentModal.open) {
              const updatedList = absentModal.list.map(l => 
                l.id === leaveId ? { ...l, status: 'approved' } : l
              );
              setAbsentModal({ ...absentModal, list: updatedList });
            }
          });
        // Отправляем событие для обновления счетчика в SidebarNav
        window.dispatchEvent(new Event('show-all-leaves'));
        alert('Заявка одобрена');
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Ошибка при одобрении заявки:', error);
      alert(`Ошибка при одобрении заявки: ${error.message}`);
    }
  };

  // Функция для отклонения заявки
  const handleRejectLeave = async (leaveId) => {
    try {
      const response = await fetch(`/api/leaves/${leaveId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'rejected' })
      });

      if (response.ok) {
        // Обновляем список заявок
        fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(data => {
            setLeaves(Array.isArray(data) ? data : []);
            // Обновляем модалку, если она открыта
            if (absentModal.open) {
              const updatedList = absentModal.list.map(l => 
                l.id === leaveId ? { ...l, status: 'rejected' } : l
              );
              setAbsentModal({ ...absentModal, list: updatedList });
            }
          });
        // Отправляем событие для обновления счетчика в SidebarNav
        window.dispatchEvent(new Event('show-all-leaves'));
        alert('Заявка отклонена');
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Ошибка при отклонении заявки:', error);
      alert(`Ошибка при отклонении заявки: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!open) return;
    
    fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setLeaves(Array.isArray(data) ? data : []));
    
    fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []);
        setUsers(Object.fromEntries(arr.map(u => [u.id, u])));
      });

    fetch('/api/congratulations/employees', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setEmployeesBirthdays(Array.isArray(data) ? data : []))
      .catch(() => setEmployeesBirthdays([]));

    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('user'));
    } catch {}
    setCurrentUser(user);
  }, [open, token]);

  useEffect(() => {
    if (!open) return;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    
    return () => {
      document.body.style.overflow = originalOverflow || '';
      document.body.style.paddingRight = originalPaddingRight || '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setAbsentModal({ open: false, date: null, list: [] });
      setShowFilters(false);
    }
  }, [open]);

  // Обработчики свайпа
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!ENABLE_MODAL_SWIPE_BACK) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 100;

    if (distance > minSwipeDistance) {
      onClose();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!open) return null;

  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  return (
    <>
      {open && ReactDOM.createPortal(
        <div
          className="nd-calmOverlay"
          style={{ zIndex: 100002 }}
          onClick={() => {
            // Закрываем модалку, возвращаемся к SidebarNav
            onClose();
          }}
        >
          <div
            ref={modalRef}
            className="nd-calMobile"
            onClick={e => e.stopPropagation()}
            {...(ENABLE_MODAL_SWIPE_BACK ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd } : {})}
          >
            {/* Заголовок с кнопкой возврата и переключателем темы */}
            <div className="nd-calmHead">
              <button
                type="button"
                className="nd-backBtn"
                onClick={onClose}
                title="Вернуться в меню"
                aria-label="Вернуться в меню"
              >
                ←
              </button>
              <h2>
                Общий календарь
                <span>отпуска · отгулы · дни рождения</span>
              </h2>
              <button
                type="button"
                className="nd-themeBtn"
                onClick={toggleNdTheme}
                title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
              >
                {ndIsLight ? '🌙' : '☀️'}
              </button>
            </div>

            {/* Переключатель вкладок */}
            <div className="nd-calmBar">
              <button
                type="button"
                className={activeTab === 'calendar' ? 'nd-calmTab nd-on' : 'nd-calmTab'}
                onClick={() => setActiveTab('calendar')}
              >
                📅 Календарь
              </button>
              <button
                type="button"
                className={activeTab === 'list' ? 'nd-calmTab nd-on' : 'nd-calmTab'}
                onClick={() => setActiveTab('list')}
              >
                📋 Список
              </button>
            </div>

            {/* Кнопка фильтров */}
            <div className="nd-calmBar">
              <button
                type="button"
                className="nd-calmFiltersBtn"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? '▼ Скрыть фильтры' : '▲ Показать фильтры'}
              </button>
            </div>

            {/* Панель фильтров */}
            {showFilters && (
              <div className="nd-calmFilters">
                {/* Месяц и год */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div className="nd-fGrp" style={{ flex: 1 }}>
                    <label htmlFor="nd-calm-month">Месяц</label>
                    <select id="nd-calm-month" value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                      {months.map((m, i) => (
                        <option key={i} value={i}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="nd-fGrp" style={{ flex: 1 }}>
                    <label htmlFor="nd-calm-year">Год</label>
                    <select id="nd-calm-year" value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                      {years.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Поиск сотрудника для HR */}
                {isHr && (
                  <div className="nd-fGrp">
                    <label htmlFor="nd-calm-emp">Сотрудник</label>
                    <input
                      id="nd-calm-emp"
                      type="text"
                      value={userSearch}
                      placeholder="Поиск по имени…"
                      onChange={e => { setUserSearch(e.target.value); setShowUserAutocomplete(true); }}
                      onFocus={() => setShowUserAutocomplete(true)}
                      onBlur={() => setTimeout(() => setShowUserAutocomplete(false), 200)}
                    />
                    {showUserAutocomplete && (
                      <div className="nd-userAc">
                        <div onMouseDown={() => { setSelectedUser('all'); setUserSearch(''); setShowUserAutocomplete(false); }}>
                          Все сотрудники
                        </div>
                        {Object.values(users)
                          .filter(u => {
                            const q = userSearch.trim().toLowerCase();
                            if (!q) return true;
                            const first = (u.first_name || '').toLowerCase();
                            const last = (u.last_name || '').toLowerCase();
                            const usern = (u.username || '').toLowerCase();
                            return usern.includes(q) || first.includes(q) || last.includes(q);
                          })
                          .slice(0, 20)
                          .map(u => (
                            <div
                              key={u.id}
                              onMouseDown={() => {
                                setSelectedUser(u.id);
                                setUserSearch(u.username || `${u.first_name || ''} ${u.last_name || ''}`.trim());
                                setShowUserAutocomplete(false);
                              }}
                            >
                              {u.username || `${u.first_name || ''} ${u.last_name || ''}`}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Тип события */}
                <div className="nd-fGrp">
                  <label>Тип события</label>
                  <div className="nd-typeChips">
                    <button
                      type="button"
                      className={selectedType === 'all' ? 'nd-tChip nd-t-all nd-on' : 'nd-tChip nd-t-all'}
                      onClick={() => setSelectedType('all')}
                    >
                      Все
                    </button>
                    {Object.entries(ND_LEAVE_TYPES).map(([key, t]) => (
                      <button
                        key={key}
                        type="button"
                        className={selectedType === key ? `nd-tChip ${t.cls} nd-on` : `nd-tChip ${t.cls}`}
                        onClick={() => setSelectedType(key)}
                      >
                        {t.emoji} {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Статус */}
                <div className="nd-fGrp">
                  <label htmlFor="nd-calm-status">Статус</label>
                  <select id="nd-calm-status" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                    <option value="all">Все</option>
                    <option value="approved">Одобрено</option>
                    <option value="pending">Ожидает</option>
                    <option value="completed">Отработано</option>
                    <option value="rejected">Отклонено</option>
                  </select>
                </div>
              </div>
            )}

            {/* Контент - Календарь или Список */}
            <div className="nd-calmBody">
              {activeTab === 'calendar' ? (
                <>
                  {/* Навигация по месяцам */}
                  <div className="nd-calmNav">
                    <button type="button" className="nd-calmNavBtn" onClick={() => handleMonthChange('prev')} aria-label="Предыдущий месяц">‹</button>
                    <div className="nd-calmNavLbl">{months[selectedMonth]} {selectedYear}</div>
                    <button type="button" className="nd-calmNavBtn" onClick={() => handleMonthChange('next')} aria-label="Следующий месяц">›</button>
                  </div>

                  {/* Календарь */}
                  <div className="nd-calWrap nd-glass">
                    <div className="nd-dowGrid">
                      {weekDays.map(d => (<div key={d} className="nd-dow">{d}</div>))}
                    </div>
                    <div className="nd-calGrid">
                      {daysMatrix.flat().map((date, i) => {
                        if (!date) return <div key={i} className="nd-cell nd-out" />;

                        const dayLeaves = filteredLeaves.filter(leave => isDateInRange(
                          date,
                          formatDateToYYYYMMDD(leave.startDate),
                          formatDateToYYYYMMDD(leave.endDate)
                        ));
                        const bdayList = getBirthdaysForDate(date);

                        return (
                          <div
                            key={i}
                            className={isToday(date) ? 'nd-cell nd-today' : 'nd-cell'}
                            style={{ animationDelay: `${Math.min(i * 8, 320)}ms` }}
                            onClick={() => {
                              const absentList = getAbsentList(date);
                              if (absentList.length > 0) {
                                setAbsentModal({ open: true, date, list: absentList });
                              }
                            }}
                          >
                            <span className="nd-dn">{date.getDate()}</span>

                            {bdayList.length > 0 && (
                              <button
                                type="button"
                                className="nd-ev nd-t-bday"
                                title={`День рождения: ${bdayList.map(e => e.last_name || e.first_name || e.username || '—').join(', ')}`}
                                onClick={(e) => { e.stopPropagation(); setBirthdayModal({ open: true, date, list: bdayList }); }}
                              >
                                🎂 {bdayList.length > 1 ? `+${bdayList.length}` : ''}
                              </button>
                            )}

                            {dayLeaves.map(leave => {
                              const t = ND_LEAVE_TYPES[getLogicalType(leave)] || ND_LEAVE_TYPES.leave;
                              const isPending = leave.status === 'pending';
                              const who = users[leave.userId]?.username || 'Сотрудник';
                              return (
                                <button
                                  key={leave.id}
                                  type="button"
                                  className={`nd-ev ${t.cls}${isPending ? ' nd-pend' : ''}`}
                                  title={`${t.label}${isPending ? ' (на одобрении)' : ''} · ${who}`}
                                  onClick={(e) => { e.stopPropagation(); setAbsentModal({ open: true, date, list: [leave] }); }}
                                >
                                  {t.emoji} {who}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Статистика */}
                  <div className="nd-legRow">
                    {Object.entries(ND_LEAVE_TYPES).map(([key, t]) => (
                      <span key={key} className={`nd-leg ${t.cls}`}>
                        <span className="nd-ic">{t.emoji}</span>
                        <span style={{ flex: 1 }}>{t.label}:</span>
                        <b>{monthlyStats[key] ?? 0}</b>
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div>
                  <h3 style={{ margin: '0 0 13px', fontSize: '13px', fontWeight: 800, color: 'var(--nd-txt)', textAlign: 'center' }}>
                    События в {months[selectedMonth]} {selectedYear}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                    {filteredLeaves
                      .filter(leave => {
                        const startDate = new Date(leave.startDate);
                        const endDate = new Date(leave.endDate);
                        const currentMonth = new Date(selectedYear, selectedMonth);
                        const nextMonth = new Date(selectedYear, selectedMonth + 1);
                        return startDate < nextMonth && endDate >= currentMonth;
                      })
                      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                      .map(leave => {
                        const logicalType = getLogicalType(leave);
                        const t = ND_LEAVE_TYPES[logicalType] || ND_LEAVE_TYPES.leave;
                        const typeText = t.label;
                        const statusKey = ND_STATUS_LABELS[leave.status] ? leave.status : 'rejected';

                        return (
                          <div
                            key={leave.id}
                            className={`nd-eRow ${t.cls}`}
                            style={{ cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch' }}
                            onClick={() => {
                              const startDate = new Date(leave.startDate);
                              const endDate = new Date(leave.endDate);
                              const dates = [];
                              for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                                dates.push(new Date(d));
                              }
                              if (dates.length > 0) {
                                setAbsentModal({ open: true, date: dates[0], list: [leave] });
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span className="nd-ty">{t.emoji} {typeText}</span>
                              <span className="nd-who" style={{ flex: 1, minWidth: 0 }}>
                                {users[leave.userId]?.username || 'Сотрудник'}
                              </span>
                              <span className={`nd-stChip nd-st-${statusKey}`}>{ND_STATUS_LABELS[statusKey]}</span>
                            </div>

                            <div className="nd-dt">
                              {String(leave.startDate).slice(0, 10)} — {String(leave.endDate).slice(0, 10)}
                            </div>

                            {leave.reason && <div className="nd-rs">{leave.reason}</div>}

                            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                              {isHr && leave.status === 'pending' && (
                                <>
                                  <button
                                    type="button"
                                    className="nd-okB"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveLeave(leave.id);
                                    }}
                                  >
                                    ✓ Утвердить
                                  </button>
                                  <button
                                    type="button"
                                    className="nd-noB"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm('Вы уверены, что хотите отклонить эту заявку?')) {
                                        handleRejectLeave(leave.id);
                                      }
                                    }}
                                  >
                                    ✕ Отклонить
                                  </button>
                                </>
                              )}
                              {(isHr || leave.userId === currentUser?.id) && (
                                <button
                                  type="button"
                                  className="nd-delB"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Удалить ${typeText.toLowerCase()}?`)) {
                                      handleDeleteLeave(leave.id);
                                    }
                                  }}
                                >
                                  🗑 Удалить
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    {filteredLeaves.filter(leave => {
                      const startDate = new Date(leave.startDate);
                      const endDate = new Date(leave.endDate);
                      const currentMonth = new Date(selectedYear, selectedMonth);
                      const nextMonth = new Date(selectedYear, selectedMonth + 1);
                      return startDate < nextMonth && endDate >= currentMonth;
                    }).length === 0 && (
                      <div className="nd-empty">
                        <div style={{ fontSize: '2em', marginBottom: '8px' }}>📅</div>
                        Нет событий в этом месяце
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Модальное окно отсутствующих */}
      {absentModal.open && ReactDOM.createPortal(
        <div className="nd-subModal" onClick={() => setAbsentModal({ open: false, date: null, list: [] })}>
          <div className="nd-mBox" style={{ width: 500 }} onClick={e => e.stopPropagation()}>
            <h3>
              <span>Отсутствующие сотрудники</span>
              <button onClick={() => setAbsentModal({ open: false, date: null, list: [] })} aria-label="Закрыть">✕</button>
            </h3>
            <div className="nd-mSub">{absentModal.date && absentModal.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <div className="nd-eList">
              {absentModal.list.map((l, idx) => {
                const logicalType = getLogicalType(l);
                const t = ND_LEAVE_TYPES[logicalType] || ND_LEAVE_TYPES.leave;
                const statusKey = ND_STATUS_LABELS[l.status] ? l.status : 'rejected';

                return (
                  <div key={idx} className={`nd-eRow ${t.cls}`} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span className="nd-ty">{t.emoji} {t.label}</span>
                      <span className="nd-who" style={{ flex: 1, minWidth: 0 }}>
                        {users[l.userId]?.username || 'Сотрудник'}
                      </span>
                      <span className={`nd-stChip nd-st-${statusKey}`}>{ND_STATUS_LABELS[statusKey]}</span>
                    </div>

                    <div className="nd-dt">
                      {String(l.startDate).slice(0, 10)} — {String(l.endDate).slice(0, 10)}
                    </div>

                    {l.reason && <div className="nd-rs">{l.reason}</div>}

                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      {isHr && l.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            className="nd-okB"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveLeave(l.id);
                            }}
                          >
                            ✓ Утвердить
                          </button>
                          <button
                            type="button"
                            className="nd-noB"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Вы уверены, что хотите отклонить эту заявку?')) {
                                handleRejectLeave(l.id);
                              }
                            }}
                          >
                            ✕ Отклонить
                          </button>
                        </>
                      )}
                      {(isHr || l.userId === currentUser?.id) && (
                        <button
                          type="button"
                          className="nd-delB"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Удалить ${t.label.toLowerCase()}?`)) {
                              handleDeleteLeave(l.id);
                              setAbsentModal({ open: false, date: null, list: [] });
                            }
                          }}
                        >
                          🗑 Удалить
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Модальное окно именинников по клику на торт */}
      {birthdayModal.open && birthdayModal.list && birthdayModal.list.length > 0 && ReactDOM.createPortal(
        <div className="nd-subModal" onClick={() => setBirthdayModal({ open: false, date: null, list: [] })}>
          <div className="nd-mBox" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
            <h3>
              <span>🎂 Именинники</span>
              <button onClick={() => setBirthdayModal({ open: false, date: null, list: [] })} aria-label="Закрыть">✕</button>
            </h3>
            <div className="nd-mSub">{birthdayModal.date ? birthdayModal.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : ''}</div>
            <ul className="nd-eList">
              {birthdayModal.list.map(e => (
                <li key={e.id} className="nd-eRow nd-t-bday">
                  <span className="nd-ty">🎂</span>
                  <span className="nd-who">
                    {e.last_name || e.first_name || e.username || '—'}
                    {(e.first_name || e.last_name) && e.username ? ` (${e.username})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

